import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@sportik/ui/primitives/sidebar';
import { ChevronLeftIcon, ChevronRightIcon, DumbbellIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { useMe } from '@/services/me';
import { useClientCount } from '@/services/roster';
import { NavUser } from './NavUser';
import { NAV_GROUPS, type NavItem } from './nav-items';

// The one departure from the stock sidebar item: the active item is a raised
// card, not the hover tint.
const ACTIVE = 'data-active:bg-card data-active:shadow-sm';

type NavMenuItemProps = {
  item: NavItem;
  /** Right-aligned count; absent until it is known. */
  count?: number;
};

function NavMenuItem({ item, count }: NavMenuItemProps) {
  const { pathname } = useLocation();
  // A screen under an item's path, a client's page under Clients, keeps the
  // item current.
  const isActive =
    item.to !== undefined &&
    (pathname === item.to || pathname.startsWith(`${item.to}/`));
  const Icon = item.icon;
  const label = (
    <>
      <Icon />
      <span>{item.label}</span>
    </>
  );

  return (
    <SidebarMenuItem>
      {item.to === undefined ? (
        // No design for this screen yet: a disabled button, so it is neither a
        // link nor focusable.
        <SidebarMenuButton disabled>{label}</SidebarMenuButton>
      ) : (
        <SidebarMenuButton
          className={ACTIVE}
          isActive={isActive}
          tooltip={item.label}
          render={
            <Link to={item.to} aria-current={isActive ? 'page' : undefined} />
          }
        >
          {label}
        </SidebarMenuButton>
      )}
      {count === undefined ? null : (
        <SidebarMenuBadge>{count}</SidebarMenuBadge>
      )}
    </SidebarMenuItem>
  );
}

/** The coach shell's navigation: brand, two groups, fold control, user. */
export function AppSidebar() {
  const { open, toggleSidebar } = useSidebar();
  const foldLabel = open ? 'Close menu' : 'Open menu';
  const clientCount = useClientCount().data;
  // Already in the cache: the shell renders only once the sign-in answered.
  const me = useMe().data;

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" render={<Link to="/" />}>
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                <DumbbellIcon className="size-4" />
              </div>
              <span className="font-semibold text-base">ROOHFit</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {NAV_GROUPS.map((group) => (
          <SidebarGroup key={group[0].label}>
            <SidebarMenu>
              {group.map((item) => (
                <NavMenuItem
                  key={item.label}
                  item={item}
                  count={item.to === '/clients' ? clientCount : undefined}
                />
              ))}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={toggleSidebar}
              aria-label={foldLabel}
              tooltip={foldLabel}
            >
              {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <NavUser
          user={{ name: me?.displayName ?? '', email: me?.email ?? '' }}
        />
      </SidebarFooter>
    </Sidebar>
  );
}
